import { BriefcaseBusiness, ExternalLink, Loader2 } from 'lucide-react'

import EmployerReviewStatusBadge from 'components/brokers/EmployerReviewStatusBadge'
import EmployerContext from 'components/context/EmployerContext'
import { Banner } from 'components/reusable/Banner'
import EmployerNotesWithDrawer from 'components/reusable/EmployerNotesWithDrawer'
import { adminEmployers } from 'rails_routes'
import { Badge } from 'slab'
import { useGetAdminEmployerProfilesEmployerProfileId, useGetAdminEmployersEmployerId } from 'utils/web_api'
import { type AdminEmployer, type AdminEmployerProfile } from 'utils/web_api.schemas'

import BankPartnersView from './BankPartnersView'
import EmployerApplicationsView from './EmployerApplicationsView'
import EmployerBasicInformation from './EmployerBasicInformation'
import EmployerChecksView from './EmployerChecksView'
import EmployerCorporateStructure from './EmployerCorporateStructure'
import EmployerDecisionModal from './EmployerDecisionModal'
import EmployerOwnersView from './EmployerOwnersView'
import EmployerPlaidAccountView from './EmployerPlaidAccountView'

interface EmployerReviewViewProps {
  employerId: string
  employerProfileId: string
}

const EmployerReviewViewImpl = ({ employer, profile }: { employer: AdminEmployer; profile: AdminEmployerProfile }) => {
  return (
    <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
      <div className='text-2xl font-semibold w-96'>Employer Review: {profile.name}</div>
      <div className='mb-4 flex items-center gap-2 flex-wrap flex-shrink-0'>
        {/* Employer Review Status Badge */}
        <EmployerReviewStatusBadge employer={employer} />

        {/* Employer Admin View Badge */}
        <Badge
          type='info'
          leftIcon={BriefcaseBusiness}
          actionIcon={ExternalLink}
          onActionClick={() => {
            window.open(adminEmployers.show({ id: employer.id }), '_blank')
          }}
        >
          Employer Admin View
        </Badge>
      </div>
      <div className='flex space-x-6'>
        <div className='flex-1 min-w-0 space-y-6'>
          <EmployerBasicInformation />
          <EmployerCorporateStructure />
          <BankPartnersView />
          <EmployerPlaidAccountView />
          <EmployerApplicationsView employerId={employer.id} />
          <EmployerOwnersView employerId={employer.id} />
          <EmployerChecksView />
          <EmployerNotesWithDrawer employerId={employer.id} />
        </div>
        <div className='w-1/4'>
          <EmployerDecisionModal employer={employer} />
        </div>
      </div>
    </div>
  )
}

const EmployerReviewView = (props: EmployerReviewViewProps) => {
  const { employerId, employerProfileId } = props

  const { data: employer, isLoading: employerLoading, error: employerError } = useGetAdminEmployersEmployerId(employerId)
  const { data: profile, isLoading: profileLoading, error: profileError } = useGetAdminEmployerProfilesEmployerProfileId(employerProfileId)

  if (employerLoading || profileLoading) {
    return (
      <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
        <Banner
          type='info'
          titleText='Loading employer data'
          overrideIcon={<Loader2 className='animate-spin mr-2' size={16} />}
        >
          Please wait while we fetch the employer information...
        </Banner>
      </div>
    )
  }

  if (employerError || profileError) {
    return (
      <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
        <Banner
          type='critical'
          titleText='Error loading employer data'
        >
          Unable to load the employer information. Please try refreshing the page or contact support if the problem persists.
        </Banner>
      </div>
    )
  }

  // At this point, we know both employer and profile are defined
  if (!employer || !profile) {
    return null
  }

  return (
    <EmployerContext.Provider value={{ employer, profile }}>
      <EmployerReviewViewImpl employer={employer} profile={profile} />
    </EmployerContext.Provider>
  )
}

export default EmployerReviewView
